import { addListener, accept } from '../../utils/event.js';
import { scale, ZERO, add } from '../../utils/vector.js';

/**
 * Millisecond duration per multiple-wheel-events batch.
 * A larger value means more onChange callbacks and fewer onBegin & onEnd
 * callbacks. Defaults to a short, implementation-specific duration.
 * Set to zero to disable batching of wheel inputs.
 *
 * Note: 200ms is a good number found through testing with a trackpad of a
 * MacBook Pro 2015, with momentum.
 */
const BATCH_DURATION = 200;
/**
 * Every wheel event is multiplied by this before sending scrub events. Wheel events are not very
 * precise and can vary greatly between devices. This value is somewhat arbitrary, but chosen to be
 * a good balance between precision and speed.
 */
const MULTIPLIER = 0.2;
function registerWheelScrub(target, ctxRef) {
    const multiplier = getDeltaMultiplier(ctxRef.current);
    let timeout = null;
    let delta = ZERO;
    const onWheel = (e) => {
        const movement = multiplier(vectorFromWheelEvent(e));
        if (timeout == null) {
            const result = ctxRef.current.onBeforeScrub?.({
                source: 'wheel',
                target: e.target,
            });
            if (result === false)
                return;
            ctxRef.current.onScrubStart?.({
                target,
                delta: ZERO,
                movement: ZERO,
                shiftKey: e.shiftKey,
                source: 'wheel',
            });
            delta = movement;
        }
        else {
            clearTimeout(timeout);
            delta = add(delta, movement);
        }
        accept(e);
        const event = {
            target,
            delta,
            movement,
            shiftKey: e.shiftKey,
            source: 'wheel',
            cancelled: false,
        };
        timeout = window.setTimeout(() => {
            timeout = null;
            if (ctxRef.current) {
                ctxRef.current.onScrubEnd?.(event);
            }
        }, BATCH_DURATION);
        ctxRef.current.onScrub?.(event);
    };
    return addListener(target, 'wheel', onWheel, { capture: true, passive: false });
}
function getDeltaMultiplier(ctx) {
    switch (ctx.singleAxis || false) {
        case 'x':
            return (v) => {
                let val = v.x;
                if (Math.abs(v.y) > Math.abs(v.x)) {
                    val = v.y;
                }
                val *= MULTIPLIER;
                return { x: val, y: 0 };
            };
        case 'y':
            return (v) => {
                let val = v.y;
                if (Math.abs(v.x) > Math.abs(v.y)) {
                    val = v.x;
                }
                val *= MULTIPLIER;
                return { x: 0, y: val };
            };
        case false:
            return (v) => scale(v, MULTIPLIER);
    }
}
function vectorFromWheelEvent(e) {
    let x = -e.deltaX;
    let y = e.deltaY;
    switch (e.deltaMode) {
        case 0 /* WheelEvent.DOM_DELTA_PIXEL */:
            break;
        case 1 /* WheelEvent.DOM_DELTA_LINE */:
            x *= 10;
            y *= 10;
            break;
        case 2 /* WheelEvent.DOM_DELTA_PAGE */:
            x *= 100;
            y *= 100;
            break;
    }
    return { x, y };
}

export { registerWheelScrub };
//# sourceMappingURL=scrub-wheel.js.map
